import React from 'react';

import {DefaultRootState} from 'react-redux';

import {MultipleStoredDataFetcher} from 'app/general/datafetchers/MultipleStoredDataFetcher';
import {StoredResponseType} from 'app/utils/redux_v2';

export type MultipleContentFetchProps<I, LP> = {
  paramsArray: Array<LP>;
  children: (
    responses: Array<StoredResponseType<I>>,
    refetch: () => Promise<any>
  ) => JSX.Element | null;
  onFetch?: (responses: Array<StoredResponseType<I>>) => unknown;
  skipInitialFetch?: boolean;
  hideLoadingIndicator?: boolean;
};

export type ContentFetchProps<I, FP> = {
  params: FP;
  children: (
    response: StoredResponseType<I>,
    refetch: () => Promise<any>
  ) => JSX.Element | null;
  onFetch?: (responses: Array<StoredResponseType<I>>) => unknown;
  skipInitialFetch?: boolean;
};

export function buildMultipleDataFetcher<I, LP, FP>(
  paramsConverter: (locationParams: LP) => FP,
  fetchDataSource: (params: FP) => Promise<any>,
  denormalizeFunc: (state: DefaultRootState, query: FP) => StoredResponseType<I>
): (props: MultipleContentFetchProps<I, LP>) => JSX.Element {
  return function PreparedMultipleStoredDataFetcher({
    paramsArray,
    children,
    onFetch,
    skipInitialFetch,
    hideLoadingIndicator,
  }: MultipleContentFetchProps<I, LP>): JSX.Element {
    return (
      <MultipleStoredDataFetcher
        fetchParamsArray={paramsArray.map(paramsConverter)}
        fetchDataSource={fetchDataSource}
        skipInitialFetch={skipInitialFetch}
        hideLoadingIndicator={hideLoadingIndicator}
        onFetch={onFetch}
        denormalizeFunc={denormalizeFunc}
      >
        {children}
      </MultipleStoredDataFetcher>
    );
  };
}

export function buildSingleDataFetcher<I, FP>(
  MultipleDataFetcherComponent: (
    props: MultipleContentFetchProps<I, FP>
  ) => JSX.Element | null
): (props: ContentFetchProps<I, FP>) => JSX.Element {
  return function PreparedSingleStoredDataFetcher({
    params,
    children,
    onFetch,
    skipInitialFetch,
  }: ContentFetchProps<I, FP>) {
    return (
      <MultipleDataFetcherComponent
        paramsArray={[params]}
        onFetch={onFetch}
        skipInitialFetch={skipInitialFetch}
      >
        {(responses, dataFetcher) => children(responses[0], dataFetcher)}
      </MultipleDataFetcherComponent>
    );
  };
}
